import { useState } from 'react';
import { projects } from '../services/api';

export default function ProjectModal({ project, onClose, onSaved }) {
  const editing = Boolean(project);
  const [form, setForm] = useState({
    name: project?.name || '', domain: project?.domain || '',
    src_host: project?.src_host || '', src_user: project?.src_user || '', src_pass: '',
    src_platform: project?.src_platform || 'cpanel',
    dst_host: project?.dst_host || '', dst_pass: '',
    dst_platform: project?.dst_platform || 'plesk', dst_user: project?.dst_user || '', dst_cpanel_pass: '',
    mig_password: '', accounts: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      if (editing) {
        await projects.update(project.id, form);
      } else {
        const accountList = form.accounts.split('\n').map(a => a.trim()).filter(Boolean);
        await projects.create({ ...form, accounts: accountList });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${editing ? 'update' : 'create'} project`);
    } finally { setLoading(false); }
  };

  const inputClass = 'w-full rounded-md border border-border2 bg-surface2 px-3 py-2 text-[13px] text-ink ' +
    'outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20';

  const inp = (label, key, type = 'text', placeholder = '') => (
    <div className="mb-3.5">
      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-dim">{label}</label>
      <input type={type} value={form[key]} placeholder={placeholder}
        onChange={e => setForm({ ...form, [key]: e.target.value })}
        className={inputClass} />
    </div>
  );

  const sectionLabel = text => (
    <div className="mt-1 border-t border-border2 pb-2 pt-3 text-[11px] font-bold uppercase tracking-wider text-dim">{text}</div>
  );

  const platformToggle = (value, onSelect, options) => (
    <div className="mb-3.5">
      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-dim">Platform</label>
      <div className="flex gap-2">
        {options.map(([p, label]) => (
          <button key={p} type="button" onClick={() => onSelect(p)}
            className={`flex-1 rounded-md border px-3 py-2 text-[13px] font-semibold transition-colors ${
              value === p ? 'border-accent bg-accent/10 text-accent' : 'border-border2 text-muted hover:text-ink'
            }`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  const warningLine = text => (
    <div className="mb-3.5 rounded-lg bg-warn/10 px-3.5 py-2.5 text-xs text-warn">{text}</div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-[820px] overflow-y-auto rounded-2xl border border-border2 bg-surface p-7 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <div className="font-display text-lg font-bold text-ink">
            {editing ? 'Edit Project' : 'New Migration Project'}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="rounded-md p-1 text-muted transition-colors hover:text-ink">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-3.5 rounded-lg bg-bad/10 px-3.5 py-2.5 text-sm text-bad">{error}</div>
        )}

        {inp('Project Name', 'name', 'text', 'Acme Corp Mail Migration')}
        {inp('Domain', 'domain', 'text', 'example.com')}

        {sectionLabel(`Source Server (${form.src_platform === 'custom' ? 'Custom IMAP' : 'cPanel'})`)}
        {platformToggle(form.src_platform, p => setForm({ ...form, src_platform: p }),
          [['cpanel', 'cPanel'], ['custom', 'Custom IMAP']])}
        <div className="grid grid-cols-2 gap-2.5">
          {inp('IP / Hostname', 'src_host', 'text', '203.0.113.10')}
          {inp(form.src_platform === 'cpanel' ? 'cPanel Password' : 'IMAP Password', 'src_pass', 'password',
            editing ? 'Leave blank to keep unchanged' : '••••••••')}
        </div>
        {form.src_platform === 'cpanel' && (
          <div className="grid grid-cols-2 gap-2.5">
            {inp('cPanel Username', 'src_user', 'text', 'acmecorp')}
          </div>
        )}
        {form.src_platform === 'custom' && warningLine(
          'This must already be the mailbox’s real, working IMAP password — no password reset is performed for a custom source.'
        )}

        {sectionLabel(`Destination Server (${
          form.dst_platform === 'cpanel' ? 'cPanel' : form.dst_platform === 'custom' ? 'Custom IMAP' : 'Plesk'
        })`)}
        {platformToggle(form.dst_platform, p => setForm({ ...form, dst_platform: p }),
          [['plesk', 'Plesk'], ['cpanel', 'cPanel'], ['custom', 'Custom IMAP']])}
        <div className="grid grid-cols-2 gap-2.5">
          {inp('IP / Hostname', 'dst_host', 'text', '198.51.100.20')}
          {inp(form.dst_platform === 'plesk' ? 'Plesk Password' : 'Server Root Password',
            'dst_pass', 'password', editing ? 'Leave blank to keep unchanged' : '••••••••')}
        </div>
        {form.dst_platform === 'cpanel' && (
          <div className="grid grid-cols-2 gap-2.5">
            {inp('cPanel Username', 'dst_user', 'text', 'webadmin')}
            {inp('cPanel Password', 'dst_cpanel_pass', 'password', editing ? 'Leave blank to keep unchanged' : '••••••••')}
          </div>
        )}
        {form.dst_platform === 'custom' && warningLine(
          'This app can’t create or reset a custom destination’s mailbox password — every account below must already exist on the destination server with its password already set to the Migration Password below.'
        )}

        {inp('Migration Password', 'mig_password', 'text', editing ? 'Leave blank to keep unchanged' : 'Leave blank to auto-generate')}

        {!editing && (
          <div className="mb-3.5">
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-dim">Accounts (one per line)</label>
            <textarea value={form.accounts} rows={5} placeholder={'jane.doe\njohn.smith\nsupport'}
              onChange={e => setForm({ ...form, accounts: e.target.value })}
              className={`${inputClass} resize-y`} />
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border2 pt-4">
          <button onClick={onClose}
            className="rounded-md border border-border2 px-4 py-1.5 text-[13px] font-semibold text-muted transition-colors hover:text-ink">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="rounded-md bg-accent px-4 py-1.5 text-[13px] font-semibold text-white transition-colors
              hover:bg-accent2 disabled:cursor-not-allowed disabled:opacity-70">
            {loading ? (editing ? 'Saving…' : 'Creating…') : (editing ? 'Save Changes' : 'Create Project →')}
          </button>
        </div>
      </div>
    </div>
  );
}
